<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import MmDialog from './MmDialog.vue'
import MmButton from './MmButton.vue'
import MmIcon from './MmIcon.vue'
import CoverArt from '../music/CoverArt.vue'

const props = defineProps<{
  modelValue: boolean
  song: { title?: string; image_url?: string; display_name?: string; creator?: { display_name?: string } } | null
  url: string
  title?: string
  pending?: boolean
  error?: string
  copied?: boolean
  actionLabel?: string
}>()
const emit = defineEmits<{ 'update:modelValue': [value: boolean]; copy: []; retry: []; shared: [] }>()
const { t } = useI18n()
const artist = computed(() => (props.song?.creator?.display_name || props.song?.display_name || '').replace(/^@/, ''))
const displayUrl = computed(() => props.url.replace(/^https?:\/\//, ''))
const twitterUrl = computed(() => `https://twitter.com/intent/tweet?${new URLSearchParams({ url: props.url, text: props.song?.title || '' })}`)
const facebookUrl = computed(() => `https://www.facebook.com/sharer/sharer.php?${new URLSearchParams({ u: props.url })}`)
const storyHelp = ref(false)
const socialError = ref(false)
watch(() => props.modelValue, () => { storyHelp.value = false; socialError.value = false })
let shareRequest = 0
onBeforeUnmount(() => { shareRequest++ })
watch([() => props.modelValue, () => props.url], () => { shareRequest++ })
const shareStory = async () => {
  if (!props.modelValue || !props.url) return
  const request = ++shareRequest, url = props.url
  // Instagram has no web Story URL intent. Use the device share chooser where
  // available; otherwise copy the real link for the user's Story link sticker.
  if (typeof navigator.share !== 'function') {
    emit('copy')
    storyHelp.value = true
    return
  }
  try { await navigator.share({ title: props.song?.title, url }); if (request === shareRequest && props.modelValue && props.url === url) emit('shared') }
  catch (error) { if (request === shareRequest && props.modelValue && (error as Error).name !== 'AbortError') socialError.value = true }
}
</script>

<template>
  <MmDialog :model-value="modelValue" :title="title || t('t24d.shareSong')" :description="t('t24d.sharePreviewDescription')" @update:model-value="emit('update:modelValue', $event)">
    <div class="dialog-body">
      <div v-if="song" class="share-song-preview"><CoverArt :src="song.image_url" alt="" :width="72" :height="72" /><div><strong>{{ song.title }}</strong><div class="muted t-small">@{{ artist }}</div></div></div>
      <p v-if="pending" role="status">{{ t('common.loading') }}</p>
      <template v-if="url">
        <div class="search share-link"><span class="t-mono t-small" :title="url">{{ displayUrl }}</span><MmButton size="sm" @click="emit('copy')"><MmIcon name="copy" :size="16" />{{ t('t24d.copy') }}</MmButton></div>
        <div class="cluster share-actions">
          <a class="btn btn-secondary btn-sm" :href="url" target="_blank" rel="noopener">{{ t('t24d.previewShare') }}<MmIcon name="arrowUR" :size="16" /></a>
          <a class="btn btn-secondary btn-sm" :href="twitterUrl" target="_blank" rel="noopener" @click="emit('shared')">{{ t('t24f.twitter') }}</a>
          <a class="btn btn-secondary btn-sm" :href="facebookUrl" target="_blank" rel="noopener" @click="emit('shared')">{{ t('t24f.facebook') }}</a>
          <MmButton size="sm" variant="secondary" @click="shareStory">{{ t('t24f.instagramStory') }}</MmButton>
        </div>
      </template>
      <p v-if="copied" role="status">{{ t('mm.feed.copied') }}</p>
      <p v-if="storyHelp" class="muted t-small" role="status">{{ t('t24f.instagramHelp') }}</p>
      <p v-if="error || socialError" role="alert">{{ error || t('ux.shareCopyFailed') }}</p>
      <MmButton v-if="!url && !pending" @click="emit('retry')">{{ actionLabel || t('ux.retry') }}</MmButton>
    </div>
  </MmDialog>
</template>

<style scoped>
.share-song-preview { display: grid; grid-template-columns: 72px minmax(0, 1fr); gap: 14px; align-items: center; margin-bottom: 16px; }
.share-link { padding-right: 4px; }
.share-link span { flex: 1; min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: var(--text-2); }
.share-actions { margin-top: 16px; }
</style>
